// Error handling utilities for consistent API error processing
import { useErrorAlert } from '../components/AlertSystem';

export interface ApiError {
  type?: string;
  value?: string;
  msg: string;
  path?: string;
  location?: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  code?: string;
  errors?: ApiError[];
}

export interface ErrorHandlerOptions {
  showAlert?: boolean;
  fallbackMessage?: string;
  fieldErrorMap?: Record<string, string>;
}

/**
 * Processes API errors and returns user-friendly error messages
 */
export const processApiError = (
  response: ApiResponse,
  options: ErrorHandlerOptions = {}
): string => {
  const {
    fallbackMessage = 'Something went wrong. Please try again.',
    fieldErrorMap = {}
  } = options;

  // Handle validation errors first (most specific)
  if (response.errors && response.errors.length > 0) {
    const firstError = response.errors[0];
    
    // Check if we have a custom message for this field
    if (firstError.path && fieldErrorMap[firstError.path]) {
      return fieldErrorMap[firstError.path];
    }
    
    // Return the validation error message
    return firstError.msg;
  }

  // Handle general error message
  if (response.message) {
    return response.message;
  }

  // Handle error property
  if (response.error) {
    return response.error;
  }

  // Fallback message
  return fallbackMessage;
};

/**
 * Hook for handling API errors with consistent messaging
 */
export const useApiErrorHandler = () => {
  const showError = useErrorAlert();

  const handleApiError = (
    response: ApiResponse,
    options: ErrorHandlerOptions = {}
  ) => {
    const errorMessage = processApiError(response, options);
    
    if (options.showAlert !== false) {
      // Determine error title based on error type
      let title = 'Error';
      
      if (response.errors) {
        const hasValidationError = response.errors.some(e => 
          ['email', 'password', 'name'].includes(e.path || '')
        );
        if (hasValidationError) {
          title = 'Validation Error';
        }
      } else if (response.message?.toLowerCase().includes('validation')) {
        title = 'Validation Error';
      } else if (response.message?.toLowerCase().includes('unauthorized')) {
        title = 'Authentication Error';
      } else if (response.message?.toLowerCase().includes('forbidden')) {
        title = 'Permission Error';
      } else if (response.message?.toLowerCase().includes('not found')) {
        title = 'Not Found';
      }

      showError(errorMessage, title);
    }

    return errorMessage;
  };

  return { handleApiError, processApiError };
};

/**
 * Common field error mappings for better user experience
 */
export const commonFieldErrors = {
  email: 'Please enter a valid email address',
  password: 'Password must contain at least one lowercase letter, one uppercase letter, and one number',
  name: 'Name must be at least 2 characters long',
  currentPassword: 'Current password is incorrect',
  newPassword: 'New password must be at least 8 characters and contain uppercase, lowercase, and numbers',
  confirmPassword: 'Passwords do not match'
};

/**
 * HTTP status code to user message mapping
 */
export const statusCodeMessages = {
  400: 'Invalid request. Please check your input.',
  401: 'Please log in to continue.',
  403: 'You don\'t have permission to perform this action.',
  404: 'The requested resource was not found.',
  409: 'This request conflicts with existing data.',
  422: 'Unable to process your request. Please check your input.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'Server error. Please try again later.',
  502: 'Service temporarily unavailable. Please try again later.',
  503: 'Service temporarily unavailable. Please try again later.',
  504: 'Request timeout. Please try again.'
};

/**
 * Determines if an error should trigger a logout (auth-related)
 */
export const shouldLogout = (response: ApiResponse, status?: number): boolean => {
  if (status === 401) return true;
  
  if (response.message?.toLowerCase().includes('token expired')) return true;
  if (response.message?.toLowerCase().includes('unauthorized')) return true;
  if (response.error?.toLowerCase().includes('token expired')) return true;
  
  return false;
};
